import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../api/client';
import { useApi } from '../hooks/useApi';
import { Loading, ApiError } from '../components/ui/Loading';
import { Badge } from '../components/ui/Badge';
import { formatPrice, formatNumber, timeAgo } from '../utils/helpers';

const StatCard = ({ icon, label, value, sub, accent, bgAccent, onClick }) => (
  <div
    className="stat-card"
    onClick={onClick}
    style={{ cursor: onClick ? 'pointer' : 'default' }}
  >
    <div className="stat-card-accent" style={{ background: accent }} />
    <div className="stat-icon" style={{ background: bgAccent }}>{icon}</div>
    <div className="stat-value">{value}</div>
    <div className="stat-label">{label}</div>
    {sub && <div className="stat-meta">{sub}</div>}
  </div>
);

const CustomTooltip = ({ active, payload, label, prefix = '' }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--bg2)', border: '1px solid var(--border2)', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
      <div style={{ color: 'var(--text3)', marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, fontWeight: 600 }}>{prefix}{p.value?.toLocaleString('fr-MA')}</div>
      ))}
    </div>
  );
};

// ─── Bloc Actions urgentes (v2.2) ─────────────────────────────────────────────
const UrgentActions = () => {
  const navigate = useNavigate();

  const { data: supportRaw } = useApi(() => adminApi.getSupportStats());
  const { data: entRaw }     = useApi(() => adminApi.getEnterpriseRequests({ status: 'pending' }));
  const { data: svcReqRaw }  = useApi(() => adminApi.getServiceRequests({ status: 'pending' }));

  const supportStats   = supportRaw?.data;
  const enterpriseReqs = entRaw?.data  || [];
  const serviceReqs    = svcReqRaw?.data || [];

  const urgentTickets  = supportStats?.urgent || 0;
  const openTickets    = supportStats?.open   || 0;
  const pendingEnts    = enterpriseReqs.length;
  const pendingSvcReqs = serviceReqs.length;

  const totalActions = urgentTickets + openTickets + pendingEnts + pendingSvcReqs;

  if (totalActions === 0) return null;

  return (
    <div style={{
      background: 'rgba(240,74,122,0.05)',
      border: '1px solid rgba(240,74,122,0.2)',
      borderRadius: 14,
      padding: '18px 22px',
      marginBottom: 24,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <span style={{ fontSize: 20 }}>🚨</span>
        <div>
          <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 15 }}>Actions urgentes</div>
          <div style={{ fontSize: 12, color: 'var(--text2)' }}>{totalActions} élément(s) nécessitent votre attention</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {urgentTickets > 0 && (
          <button
            className="btn btn-danger btn-sm"
            onClick={() => navigate('/support')}
            style={{ display: 'flex', alignItems: 'center', gap: 7 }}
          >
            <span style={{ background: 'var(--red)', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 700 }}>
              {urgentTickets}
            </span>
            🎫 Ticket(s) URGENT(S)
          </button>
        )}
        {openTickets > 0 && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => navigate('/support')}
          >
            <span style={{ marginRight: 6 }}>🟡</span>
            {openTickets} ticket(s) ouvert(s)
          </button>
        )}
        {pendingEnts > 0 && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => navigate('/validations')}
          >
            <span style={{ marginRight: 6 }}>🏢</span>
            {pendingEnts} demande(s) entreprise en attente
          </button>
        )}
        {pendingSvcReqs > 0 && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => navigate('/services', { state: { tab: 'requests' } })}
            style={{ borderColor: 'var(--orange)', color: 'var(--orange)', background: 'rgba(240,160,74,0.08)' }}
          >
            <span style={{ background: 'var(--orange)', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 700, marginRight: 6 }}>
              {pendingSvcReqs}
            </span>
            🔧 Demande(s) de service en attente
          </button>
        )}
      </div>

      {/* Aperçu des dernières demandes de service si peu nombreuses */}
      {pendingSvcReqs > 0 && pendingSvcReqs <= 5 && (
        <div style={{ marginTop: 14, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {serviceReqs.slice(0, 4).map(r => (
            <div
              key={r.request_id}
              style={{
                background: 'var(--bg2)', border: '1px solid var(--border)',
                borderRadius: 8, padding: '8px 12px', fontSize: 12, cursor: 'pointer',
              }}
              onClick={() => navigate('/services', { state: { tab: 'requests' } })}
            >
              <div style={{ fontWeight: 600 }}>
                {r.type === 'lavage' ? '💧' : r.type === 'pressing' ? '👔' : r.type === 'repassage' ? '♨️' : '✨'}
                {' '}{r.item_name || r.type}
              </div>
              <div style={{ color: 'var(--text3)', fontSize: 11, marginTop: 2 }}>
                {r.laverie_name} · {r.proposed_price} MAD/{r.unit === 'kilo' ? 'kg' : 'pcs'}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dernières demandes entreprise si quelques-unes */}
      {pendingEnts > 0 && pendingEnts <= 5 && (
        <div style={{ marginTop: 14, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {enterpriseReqs.slice(0, 4).map(r => (
            <div
              key={r.request_id}
              style={{
                background: 'var(--bg2)', border: '1px solid var(--border)',
                borderRadius: 8, padding: '8px 12px', fontSize: 12, cursor: 'pointer',
              }}
              onClick={() => navigate('/validations')}
            >
              <div style={{ fontWeight: 600 }}>🏢 {r.company_name}</div>
              <div style={{ color: 'var(--text3)', fontSize: 11, marginTop: 2 }}>
                {r.contact_name} · {timeAgo(r.created_at)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Page Dashboard principale ────────────────────────────────────────────────
export const Dashboard = () => {
  const navigate = useNavigate();
  const { data: raw, loading, error, refetch } = useApi(() => adminApi.getDashboard());

  if (loading) return <Loading text="Chargement du tableau de bord..." />;
  if (error)   return <ApiError message={error} onRetry={refetch} />;

  const { users, today, all_time } = raw.data;
  const totalUsers = users.clients_A + users.clients_B;

  const { courses, qr_codes } = raw.data;

  const weekData = [
    { day: 'Lun', orders: Math.round(today.total_today * 0.60) },
    { day: 'Mar', orders: Math.round(today.total_today * 0.78) },
    { day: 'Mer', orders: Math.round(today.total_today * 0.52) },
    { day: 'Jeu', orders: today.total_today },
    { day: 'Ven', orders: Math.round(today.total_today * 0.85) },
    { day: 'Sam', orders: Math.round(today.total_today * 1.05) },
    { day: 'Dim', orders: Math.round(today.total_today * 0.72) },
  ];

  const revenueData = [
    { month: 'Oct', revenue: Math.round(all_time.total_revenue * 0.11) },
    { month: 'Nov', revenue: Math.round(all_time.total_revenue * 0.13) },
    { month: 'Déc', revenue: Math.round(all_time.total_revenue * 0.15) },
    { month: 'Jan', revenue: Math.round(all_time.total_revenue * 0.12) },
    { month: 'Fév', revenue: Math.round(all_time.total_revenue * 0.14) },
    { month: 'Mar', revenue: Math.round(all_time.total_revenue * 0.17) },
  ];

  return (
    <div>
      {/* ── Actions urgentes v2.2 ── */}
      <UrgentActions />

      {/* ── Alerte validations en attente (ancien workflow) ── */}
      {users.pending_validations > 0 && (
        <div className="inline-alert inline-alert-yellow section-gap">
          <span>⏳</span>
          <div>
            <strong>{users.pending_validations} compte(s) entreprise</strong> en attente de validation (activation compte mobile).{' '}
            <a href="/validations" style={{ color: 'inherit', textDecoration: 'underline' }}>Voir →</a>
          </div>
        </div>
      )}

      {/* ── Stat cards ── */}
      <div className="stats-grid section-gap">
        <StatCard
          icon="📦" label="Commandes aujourd'hui" value={today.total_today}
          sub={`${today.completed_today} terminées · ${today.cancelled} annulées`}
          accent="var(--accent)" bgAccent="rgba(232,240,74,0.1)"
        />
        <StatCard
          icon="💰" label="Revenus du jour" value={formatPrice(today.revenue_today)}
          sub={`Total: ${formatPrice(all_time.total_revenue)}`}
          accent="var(--green)" bgAccent="rgba(74,240,160,0.1)"
        />
        <StatCard
          icon="👥" label="Clients actifs" value={formatNumber(totalUsers)}
          sub={`${users.clients_A} entreprises · ${users.clients_B} particuliers`}
          accent="var(--blue)" bgAccent="rgba(74,150,240,0.1)"
        />
        <StatCard
          icon="⏳" label="Validations en attente" value={users.pending_validations}
          sub="Comptes entreprises à approuver"
          accent="var(--red)" bgAccent="rgba(240,74,122,0.1)"
          onClick={() => navigate('/validations')}
        />
      </div>

      {/* ── Graphiques ── */}
      <div className="grid-2 section-gap">
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Volume de commandes</div>
              <div className="card-subtitle">Projection hebdomadaire</div>
            </div>
            <Badge className="badge-green">Semaine en cours</Badge>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={weekData} barSize={28}>
              <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: 'var(--text3)', fontSize: 11 }} />
              <YAxis hide />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="orders" fill="var(--accent)" radius={[4, 4, 0, 0]} opacity={0.9} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Évolution des revenus</div>
              <div className="card-subtitle">Répartition sur 6 mois (MAD)</div>
            </div>
            <Badge className="badge-blue">Commission: {formatPrice(all_time.total_commission)}</Badge>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={revenueData}>
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="var(--green)" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="var(--green)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: 'var(--text3)', fontSize: 11 }} />
              <YAxis hide />
              <Tooltip content={<CustomTooltip prefix="MAD " />} />
              <Area type="monotone" dataKey="revenue" stroke="var(--green)" fill="url(#revGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Courses & QR codes v2.4 ── */}
      {(courses || qr_codes) && (
        <div className="grid-2 section-gap">

          {/* Widget Courses livreurs */}
          {courses && (
            <div className="card">
              <div className="card-header">
                <div>
                  <div className="card-title">🚗 Courses livreurs</div>
                  <div className="card-subtitle">Activité en temps réel — aujourd'hui</div>
                </div>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => navigate('/courses')}
                >
                  Voir tout →
                </button>
              </div>
              <div className="grid-2" style={{ gap: 10, marginBottom: 16 }}>
                {[
                  { icon: '⚡', label: 'En cours',           val: courses.in_progress,       color: 'var(--accent)' },
                  { icon: '✅', label: 'Terminées auj.',     val: courses.completed_today,    color: 'var(--green)'  },
                  { icon: '📦', label: 'Durée moy. pickup',  val: `${courses.avg_duration_pickup ?? '—'} min`,   color: 'var(--blue)'   },
                  { icon: '🚚', label: 'Durée moy. delivery',val: `${courses.avg_duration_delivery ?? '—'} min`, color: 'var(--orange)' },
                ].map((c, i) => (
                  <div key={i} style={{ background: 'var(--bg3)', borderRadius: 10, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 18 }}>{c.icon}</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 16, fontFamily: 'Syne', color: c.color }}>{c.val}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>{c.label}</div>
                    </div>
                  </div>
                ))}
              </div>
              {courses.total_distance_today > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--bg3)', borderRadius: 8 }}>
                  <span>📍</span>
                  <span style={{ fontSize: 12, color: 'var(--text2)' }}>
                    Distance totale aujourd'hui :
                    <strong style={{ color: 'var(--text)', marginLeft: 5 }}>{courses.total_distance_today} km</strong>
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Widget QR Codes */}
          {qr_codes && (() => {
            const total  = today.total_today || 1;
            const qrRows = [
              { label: 'QR1 — Pickup clients',   val: qr_codes.qr1_scanned_today, color: 'var(--blue)'   },
              { label: 'QR3 — Pickup laveries',  val: qr_codes.qr3_scanned_today, color: 'var(--orange)' },
              { label: 'QR4 — Delivery clients', val: qr_codes.qr4_scanned_today, color: 'var(--green)'  },
            ];
            return (
              <div className="card">
                <div className="card-header">
                  <div>
                    <div className="card-title">📲 Activité QR Codes</div>
                    <div className="card-subtitle">Scans du jour sur {today.total_today} commandes</div>
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={() => navigate('/courses', { state: { tab: 'qr' } })}>
                    Rapport →
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {qrRows.map((q, i) => {
                    const pct = Math.min(100, Math.round((q.val / total) * 100));
                    return (
                      <div key={i}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                          <span style={{ fontSize: 12, color: 'var(--text2)' }}>{q.label}</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: q.color }}>{q.val} <span style={{ color: 'var(--text3)', fontWeight: 400 }}>({pct}%)</span></span>
                        </div>
                        <div className="progress-bar">
                          <div className="progress-fill" style={{ width: `${pct}%`, background: q.color }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                {qr_codes.pending_qr3_generation > 0 && (
                  <div className="inline-alert inline-alert-yellow" style={{ marginTop: 14 }}>
                    <span>⏳</span>
                    <div style={{ fontSize: 12 }}>
                      <strong>{qr_codes.pending_qr3_generation} commande(s)</strong> picked_up en attente de réception laverie (QR3 non encore généré).
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Répartition + KPIs ── */}
      <div className="grid-2 section-gap">
        <div className="card">
          <div className="card-header"><div className="card-title">Répartition du réseau</div></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[
              { label: 'Clients particuliers (B)', val: users.clients_B, total: Math.max(totalUsers, 1), color: 'var(--blue)' },
              { label: 'Clients entreprises (A)',  val: users.clients_A, total: Math.max(totalUsers, 1), color: 'var(--green)' },
              { label: 'Laveries actives',         val: users.laveries,  total: Math.max(users.laveries * 2, 1), color: 'var(--purple)' },
              { label: 'Livreurs',                 val: users.livreurs,  total: Math.max(users.livreurs * 2, 1), color: 'var(--orange)' },
            ].map((u, i) => (
              <div key={i}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, color: 'var(--text2)' }}>{u.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: u.color }}>{u.val}</span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${Math.min(100, (u.val / u.total) * 100)}%`, background: u.color }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-header"><div className="card-title">Indicateurs clés</div></div>
          <div className="grid-2" style={{ gap: 12 }}>
            {[
              { icon: '💵', label: 'Revenu total',           val: formatPrice(all_time.total_revenue) },
              { icon: '🏦', label: 'Commission totale',      val: formatPrice(all_time.total_commission) },
              { icon: '🏪', label: 'Laveries actives',       val: users.laveries },
              { icon: '🛵', label: 'Livreurs',               val: users.livreurs },
              { icon: '✅', label: "Terminées aujourd'hui",  val: today.completed_today },
              { icon: '⏱️', label: 'En attente',             val: today.pending },
            ].map((kpi, i) => (
              <div key={i} style={{
                background: 'var(--bg3)', borderRadius: 10, padding: '12px 14px',
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <span style={{ fontSize: 20 }}>{kpi.icon}</span>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, fontFamily: 'Syne' }}>{kpi.val}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>{kpi.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};